import moment from 'moment';

import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { CHAT_MESSAGE } from '@app/constants/clientsHistoryActionTypes';

import {
  LEAD_MSG_STATUS_DELETED,
  LEAD_MSG_STATUS_PENDING,
} from './chatMessageStatus';
import { compareRecommendRevisions } from './compareRecommendRevisions';

export const getLeadHistoryStableKey = (
  item: LeadCardHistoryItemData
): string => {
  const recommend = item.ChatMessages?.[0]?.RecommendChat;
  if (recommend) {
    return `RecommendChat_${recommend.conversationId}_${recommend.messageId}`;
  }

  if (item.IdS) {
    return [item.ObjectType ?? 'unknown', item.IdS].join('_');
  }

  const messageId = item.ChatMessages?.[0]?.MessageId;

  if (messageId) {
    return ['ChatMessage', messageId].join('_');
  }

  // У строк без IdS и MessageId ключ собирается из контента: два системных
  // действия в одну секунду по одному объекту (например, массовое назначение
  // тегов при создании клиента) различаются только ActionText/ActionItems —
  // без них одна из строк молча перезаписывала другую при merge.
  const actionItemsKey = (item.ActionItems ?? [])
    .map(
      ({ Property, ValueOld, ValueNew }) =>
        `${Property ?? ''}:${ValueOld ?? ''}>${ValueNew ?? ''}`
    )
    .join('|');

  return [
    item.ActionId ?? 'noAction',
    item.Timestamp ?? 'noTime',
    item.ObjectType ?? 'unknown',
    item.ObjectId ?? 'noObject',
    item.ActionUserId ?? 'noUser',
    item.ActionText ?? '',
    actionItemsKey,
  ].join('_');
};

// Timestamp приходит с точностью до секунды: системное действие и его
// следствия (создание клиента + назначение тегов) часто совпадают по времени.
// При равных Timestamp порядок задают tie-breaker'ы, а не порядок ответа бэка.
const getLeadHistoryKindRank = (item: LeadCardHistoryItemData): number =>
  item.ActionId === CHAT_MESSAGE ? 1 : 0;

// IdS у строк истории — строка с числовым хвостом ("client-change-137130"),
// который растёт в порядке записи на бэке. Сравнивать хвосты можно только
// внутри одной группы (одинаковый префикс), чтобы не мешать разные сущности.
const ID_WITH_NUMERIC_SUFFIX = /^(.*?)(\d+)$/;

const getLeadHistoryOrderId = (
  item: LeadCardHistoryItemData
): { group: string; id: number | string } | null => {
  const recommend = item.ChatMessages?.[0]?.RecommendChat;
  if (recommend) {
    return {
      group: `RecommendChat_${recommend.conversationId}`,
      id: recommend.sequence,
    };
  }

  const messageId = item.ChatMessages?.[0]?.MessageId;

  if (messageId) {
    return { group: 'ChatMessage', id: messageId };
  }

  if (item.IdS) {
    const match = ID_WITH_NUMERIC_SUFFIX.exec(item.IdS);

    if (match) {
      return { group: match[1], id: Number(match[2]) };
    }
  }

  return null;
};

const getLeadHistoryTimestampValue = (timestamp: string): number => {
  const parsed = Date.parse(timestamp);

  if (!Number.isNaN(parsed)) return parsed;

  const momentValue = moment(timestamp).valueOf();

  return Number.isNaN(momentValue) ? 0 : momentValue;
};

// Сортировка идёт по заранее вычисленному на строку кортежу — лексикографическое
// сравнение кортежей транзитивно по построению. Попарные же ветвления
// («если у обеих строк есть id — по id, иначе по порядку прихода») ломают
// транзитивность: цикл A<B<C<A делает результат sort зависимым от порядка
// входа. Группа строк с одинаковым префиксом IdS встаёт на позицию самого
// раннего своего представителя (anchorSeq) и внутри упорядочивается по id.
interface LeadHistorySortMeta {
  timestampValue: number;
  kindRank: number;
  anchorSeq: number;
  inGroupId: number | string;
  firstSeenSeq: number;
  stableKey: string;
}

const buildLeadHistorySortMeta = (
  items: LeadCardHistoryItemData[],
  firstSeenSeq: Map<string, number>
): Map<string, LeadHistorySortMeta> => {
  const groupAnchorSeq = new Map<string, number>();
  const groupKeyByStableKey = new Map<string, string | null>();

  for (const item of items) {
    const stableKey = getLeadHistoryStableKey(item);
    const orderId = getLeadHistoryOrderId(item);

    if (!orderId) {
      groupKeyByStableKey.set(stableKey, null);
      continue;
    }

    const groupKey = [
      getLeadHistoryTimestampValue(item.Timestamp),
      getLeadHistoryKindRank(item),
      orderId.group,
    ].join('|');
    groupKeyByStableKey.set(stableKey, groupKey);

    const seq = firstSeenSeq.get(stableKey) ?? 0;
    const knownAnchor = groupAnchorSeq.get(groupKey);

    if (knownAnchor === undefined || seq < knownAnchor) {
      groupAnchorSeq.set(groupKey, seq);
    }
  }

  const metaByStableKey = new Map<string, LeadHistorySortMeta>();

  for (const item of items) {
    const stableKey = getLeadHistoryStableKey(item);
    const orderId = getLeadHistoryOrderId(item);
    const seq = firstSeenSeq.get(stableKey) ?? 0;
    const groupKey = groupKeyByStableKey.get(stableKey);
    const anchorSeq =
      groupKey !== null && groupKey !== undefined
        ? groupAnchorSeq.get(groupKey) ?? seq
        : seq;

    metaByStableKey.set(stableKey, {
      timestampValue: getLeadHistoryTimestampValue(item.Timestamp),
      kindRank: getLeadHistoryKindRank(item),
      anchorSeq,
      inGroupId: orderId?.id ?? 0,
      firstSeenSeq: seq,
      stableKey,
    });
  }

  return metaByStableKey;
};

const compareLeadHistorySortMeta = (
  a: LeadHistorySortMeta,
  b: LeadHistorySortMeta
): number =>
  a.timestampValue - b.timestampValue ||
  a.kindRank - b.kindRank ||
  a.anchorSeq - b.anchorSeq ||
  (typeof a.inGroupId === 'string' && typeof b.inGroupId === 'string'
    ? compareRecommendRevisions(a.inGroupId, b.inGroupId) ?? 0
    : Number(a.inGroupId) - Number(b.inGroupId)) ||
  a.firstSeenSeq - b.firstSeenSeq ||
  (a.stableKey < b.stableKey ? -1 : a.stableKey > b.stableKey ? 1 : 0);

const getLeadHistoryChatMessageStatus = (
  item: LeadCardHistoryItemData
): string | null => item.ChatMessages?.[0]?.Status ?? null;

// Бэк никогда не возвращает статус сообщения в pending (повторная отправка
// статус не сбрасывает) и не «воскрешает» deleted. Строка, понижающая уже
// показанный статус до pending или снимающая deleted, — это устаревший
// HTTP-снапшот истории, обогнанный WS-событием: такую строку игнорируем.
// Остальные переходы (sent→error, error→delivered и т.п.) на бэке легальны
// в обе стороны, поэтому не блокируются.
const isStaleChatMessageUpdate = (
  prev: LeadCardHistoryItemData,
  incoming: LeadCardHistoryItemData
): boolean => {
  const prevStatus = getLeadHistoryChatMessageStatus(prev);

  if (!prevStatus) return false;

  const incomingStatus = getLeadHistoryChatMessageStatus(incoming);

  if (
    prevStatus !== LEAD_MSG_STATUS_PENDING &&
    incomingStatus === LEAD_MSG_STATUS_PENDING
  ) {
    return true;
  }

  return (
    prevStatus === LEAD_MSG_STATUS_DELETED &&
    incomingStatus !== LEAD_MSG_STATUS_DELETED
  );
};

const mergeRecommendUpdate = (
  prev: LeadCardHistoryItemData,
  incoming: LeadCardHistoryItemData
): LeadCardHistoryItemData | null => {
  const previousMetadata = prev.ChatMessages?.[0]?.RecommendChat;
  const incomingMetadata = incoming.ChatMessages?.[0]?.RecommendChat;
  if (!previousMetadata || !incomingMetadata) return null;

  const comparison = compareRecommendRevisions(
    incomingMetadata.revision,
    previousMetadata.revision
  );
  const base = comparison !== null && comparison > 0 ? incoming : prev;
  const baseMessage = base.ChatMessages?.[0];
  const baseMetadata = baseMessage?.RecommendChat;
  if (!baseMessage || !baseMetadata) return base;

  // CRM read-operation receipts have their own lifecycle: a rejection must
  // clear the matching pending operation without inventing a core revision.
  const hasMatchingTerminalOperation =
    Boolean(incomingMetadata.readOperationId) &&
    (previousMetadata.readOperationId === incomingMetadata.readOperationId ||
      previousMetadata.readOperationId === null ||
      (comparison !== null && comparison >= 0)) &&
    (incomingMetadata.readOperationStatus === 'applied' ||
      incomingMetadata.readOperationStatus === 'rejected');
  const isPendingOperationAlreadyTerminal =
    previousMetadata.readOperationId === incomingMetadata.readOperationId &&
    (previousMetadata.readOperationStatus === 'applied' ||
      previousMetadata.readOperationStatus === 'rejected');
  const readMetadata = hasMatchingTerminalOperation
    ? incomingMetadata
    : incomingMetadata.staffReadPending && !isPendingOperationAlreadyTerminal
    ? incomingMetadata
    : previousMetadata.staffReadPending &&
      baseMetadata.recipientState !== 'read'
    ? previousMetadata
    : baseMetadata;

  return {
    ...base,
    ChatMessages: [
      {
        ...baseMessage,
        RecommendChat: {
          ...baseMetadata,
          staffReadPending:
            baseMetadata.recipientState === 'read'
              ? false
              : readMetadata.staffReadPending,
          readOperationId: readMetadata.readOperationId,
          readOperationStatus: readMetadata.readOperationStatus,
          readOperationErrorCode: readMetadata.readOperationErrorCode,
        },
      },
      ...(base.ChatMessages?.slice(1) ?? []),
    ],
  };
};

export const mergeLeadHistoryByStableKey = (
  prev: LeadCardHistoryItemData[],
  incoming: LeadCardHistoryItemData[],
  firstSeenSeq: Map<string, number>,
  nextSeqRef: { current: number }
): LeadCardHistoryItemData[] => {
  const map = new Map<string, LeadCardHistoryItemData>();
  const pendingCrmKeys = new Map<string, string>();
  const canonicalCrmKeys = new Set<string>();

  for (const item of prev) {
    const key = getLeadHistoryStableKey(item);
    map.set(key, item);
    const message = item.ChatMessages?.[0];
    if (message && !message.RecommendChat) {
      pendingCrmKeys.set(
        `${message.SocialIntegrationId}:${message.MessageId}`,
        key
      );
    } else if (message?.RecommendChat) {
      canonicalCrmKeys.add(
        `${message.SocialIntegrationId}:${message.MessageId}`
      );
    }
    if (!firstSeenSeq.has(key)) {
      firstSeenSeq.set(key, nextSeqRef.current++);
    }
  }

  for (const item of incoming) {
    const key = getLeadHistoryStableKey(item);
    const message = item.ChatMessages?.[0];
    if (
      message &&
      !message.RecommendChat &&
      canonicalCrmKeys.has(
        `${message.SocialIntegrationId}:${message.MessageId}`
      )
    )
      continue;
    if (message && !message.RecommendChat)
      pendingCrmKeys.set(
        `${message.SocialIntegrationId}:${message.MessageId}`,
        key
      );
    if (message?.RecommendChat)
      canonicalCrmKeys.add(
        `${message.SocialIntegrationId}:${message.MessageId}`
      );
    const previousPendingKey = message?.RecommendChat
      ? pendingCrmKeys.get(
          `${message.SocialIntegrationId}:${message.MessageId}`
        )
      : undefined;
    if (previousPendingKey && previousPendingKey !== key) {
      // CRM can persist the outgoing row before core allocates its sequence.
      // Arrival of canonical metadata enriches that row, not a second bubble.
      map.delete(previousPendingKey);
      const previousSequence = firstSeenSeq.get(previousPendingKey);
      if (previousSequence !== undefined && !firstSeenSeq.has(key))
        firstSeenSeq.set(key, previousSequence);
    }
    if (!firstSeenSeq.has(key)) {
      firstSeenSeq.set(key, nextSeqRef.current++);
    }

    const knownItem = map.get(key);

    if (knownItem) {
      const recommendUpdate = mergeRecommendUpdate(knownItem, item);
      if (recommendUpdate) {
        map.set(key, recommendUpdate);
        continue;
      }
    }

    if (knownItem && isStaleChatMessageUpdate(knownItem, item)) {
      continue;
    }

    map.set(key, item);
  }

  const out = Array.from(map.values());
  const metaByStableKey = buildLeadHistorySortMeta(out, firstSeenSeq);
  const fallbackMeta: LeadHistorySortMeta = {
    timestampValue: 0,
    kindRank: 0,
    anchorSeq: 0,
    inGroupId: 0,
    firstSeenSeq: 0,
    stableKey: '',
  };

  out.sort((a, b) =>
    compareLeadHistorySortMeta(
      metaByStableKey.get(getLeadHistoryStableKey(a)) ?? fallbackMeta,
      metaByStableKey.get(getLeadHistoryStableKey(b)) ?? fallbackMeta
    )
  );

  // System notifications may have a historical occurredAt. Keep unrelated
  // channels in their chronological slots, while each Recommend conversation
  // uses its canonical sequence. A pairwise date/sequence comparator would
  // violate transitivity across mixed channels.
  const conversationSlots = new Map<string, number[]>();
  out.forEach((item, index) => {
    const metadata = item.ChatMessages?.[0]?.RecommendChat;
    if (!metadata) return;
    const slots = conversationSlots.get(metadata.conversationId) ?? [];
    slots.push(index);
    conversationSlots.set(metadata.conversationId, slots);
  });
  for (const slots of conversationSlots.values()) {
    const ordered = slots
      .map((index) => out[index])
      .sort(
        (a, b) =>
          compareRecommendRevisions(
            a.ChatMessages![0].RecommendChat!.sequence,
            b.ChatMessages![0].RecommendChat!.sequence
          ) ?? 0
      );
    slots.forEach((index, position) => {
      out[index] = ordered[position];
    });
  }

  return out;
};
